import type { HistorySessionFragment } from "api/generated";
import AssignmentBox, { type IAssignment } from "components/AssignmentBox/AssignmentBox";
import { MOOD_ICONS } from "const";
import StrawberryIcon from "images/icons/18-strawberry.svg";
import { useMemo } from "react";
import { getAdmissionTestLabel } from "utils/common/assignmentDetail";

interface Props {
  session: HistorySessionFragment;
  date: string;
}

export const SessionHistory = ({ session, date }: Props) => {
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString("cs-CZ");

  const getResultDotColor = (scoreReal?: number | null, scoreMax?: number | null) => {
    if (scoreReal === null || scoreReal === undefined || scoreMax === null || scoreMax === undefined) return undefined;
    if (scoreReal <= 0) return "red";
    if (scoreReal >= scoreMax) return "green";

    return "yellow";
  };

  const calculateSuccessRate = () => {
    if (!session.scoreRealTotal || !session.scoreMaxTotal) return;

    return Math.round((session.scoreRealTotal / session.scoreMaxTotal) * 100);
  };

  const successRate = calculateSuccessRate();
  const moodIcon = MOOD_ICONS.find(icon => icon.value === session.lastSessionMood)?.name;

  const assignments = useMemo<IAssignment[]>(
    () =>
      session.userAssignments.map(
        assignment =>
          ({
            id: assignment.id,
            text: assignment.assignment?.assignment?.assignmentText ?? "",
            href: `/historie/cviceni/${session.id}/${assignment.id}`,
            testId: getAdmissionTestLabel(assignment.assignment?.general?.admissionTest) ?? null,
            testOrder: assignment.assignment?.general?.testOrder ?? null,
            dotColor: getResultDotColor(assignment.scoreReal, assignment.scoreMax),
          }) satisfies IAssignment,
      ),
    [session],
  );

  const getEnvelopeChange = (assignmentId: string) => {
    const movement = session.envelopeMovements.find(movement => movement.userAssigment === assignmentId);

    const envelopeNow = movement?.envelopeNow;
    if (!envelopeNow) return;

    return {
      ...movement,
      envelopeNow,
    };
  };

  return (
    <div className="history">
      <div className="history__header history__header--session">
        <div className="d-flex flex-direction-row gap-2">
          <StrawberryIcon />
          <div>
            <strong>Trénink {formatDate(date)}</strong>
          </div>
          {(successRate ?? moodIcon) ? (
            <div className="ms-auto d-flex flex-direction-row gap-3 align-items-center">
              {successRate ? (
                <div>
                  úspěšnost: <strong>{successRate} %</strong>
                </div>
              ) : null}
              {moodIcon ? <div>{moodIcon}</div> : null}
            </div>
          ) : null}
        </div>
        <div className="ps-4">
          {session.lastSessionMoodText ? (
            <div className="d-flex gap-2">
              <strong>+</strong>
              <i>„{session.lastSessionMoodText}“</i>
            </div>
          ) : null}
          {session.lastSessionMoodSelfReflection ? (
            <div className="d-flex gap-2">
              <strong>-</strong>
              <i>„{session.lastSessionMoodSelfReflection}“</i>
            </div>
          ) : null}
        </div>
      </div>

      {assignments.map(assignment => (
        <AssignmentBox key={assignment.id} assignment={assignment} envelopeChange={getEnvelopeChange(assignment.id)} />
      ))}
    </div>
  );
};
